import { Execution } from "@/shared/Executions/models/Execution";
import { HierarchicExecutionViewModel } from "../models/HierarchicExecutionViewModel";
import { AdvancedAnalysisPhaseType, ExecutionPhaseType } from "@/shared/Executions/models/ExecutionPhaseType";
import { ExecutionContextModel } from "@/components/AnalysisWizard/models/ExecutionContextModel";
import { IExecutionPhaseService } from "@/shared/Executions/services/ExecutionPhaseService";

export class HierarchicExecutionService {

    // Currenty we do not impl any custom logic for enforcing number of tiers or type of execution phase at tier
    // and strictly render what the API returns
    public static buildHierarchicArray(flatList: Array<Execution>, executionPhaseService: IExecutionPhaseService): Array<HierarchicExecutionViewModel> {

        // Return early for empty input
        if (!Array.isArray(flatList) || flatList.length === 0) return [];

        // Work on shallow clones so we don't mutate caller-owned objects
        flatList = flatList.map(item => ({ ...item }));

        // Build id -> item map (do NOT pre-initialize children)
        const idMap = new Map<string, HierarchicExecutionViewModel>();
        flatList.forEach(item => {
            if (item.id) idMap.set(item.id, item);
        });

        // Identify original roots: id exists, rootId === id, and parentId is null/undefined
        const originalRoots: HierarchicExecutionViewModel[] = flatList.filter(item =>
            !!item.id && item.rootId === item.id && (item.parentId === null || item.parentId === undefined)
        );

        // Track attached items to avoid duplicates
        const attachedIds = new Set<string>();

        // Helper to detect cycles: if attaching `childId` under `parent` would make
        // the child appear in its own ancestor chain, return true.
        const createsCycle = (childId: string | undefined | null, parent?: HierarchicExecutionViewModel): boolean => {
            if (!childId || !parent) return false;
            let cur: HierarchicExecutionViewModel | undefined | null = parent;
            while (cur) {
                if (cur.id === childId) return true;
                if (!cur.parentId) break;
                cur = idMap.get(cur.parentId);
            }
            return false;
        };

        // Attach each non-root item to its parent if available. If parent is missing (orphan),
        // promote the item to a top-level root. If attaching would create a cycle, promote to root.
        flatList.forEach(item => {
            // Skip items that are represented as final roots
            if (originalRoots.includes(item)) return;

            // If already attached (by id) skip
            if (item.id && attachedIds.has(item.id)) return;

            let attached = false;

            if (item.parentId) {
                const parent = idMap.get(item.parentId);
                if (parent && parent !== item) {
                    if (!createsCycle(item.id, parent)) {
                        parent.children = parent.children ?? [];
                        parent.children.push(item);
                        attached = true;
                        if (item.id) attachedIds.add(item.id);
                    } else {
                        // Cycle detected: fall through to promote as root
                    }
                }
            }

            // If not attached (no parent, parent missing, or cycle), treat as a top-level root
            if (!attached) {
                if (!originalRoots.includes(item)) {
                    originalRoots.push(item);
                }
                if (item.id) attachedIds.add(item.id);
            }
        });

        // Set operableInputFileId: roots -> their own input?.fileId; non-roots -> top-most parent's (root) input?.fileId
        const propagateOperable = (node?: HierarchicExecutionViewModel, rootFileId?: string) => {
            if (!node) return;
            const fileIdForRoot = rootFileId === undefined ? node.input?.fileId : rootFileId;
            node.operableInputFileId = fileIdForRoot;
            if (node.children && node.children.length > 0) {
                node.children.forEach(child => propagateOperable(child as HierarchicExecutionViewModel, fileIdForRoot));
            }
        };

        originalRoots.forEach(root => propagateOperable(root));

        // Annotate primaryExecutionPhase for every node using the provided executionPhaseService
        if (executionPhaseService && typeof executionPhaseService.tryGetPrimaryPhaseTypeForExecution === "function") {
            idMap.forEach(node => {
                node.primaryExecutionPhase = ((node?.progress?.totalPhases ?? 0) > 1) ? AdvancedAnalysisPhaseType : executionPhaseService.tryGetPrimaryPhaseTypeForExecution(node);
            });
        }

        // Delegate ordering (including recursive child ordering) to the centralized sorter
        return this.orderByCreatedDateDescending(originalRoots);
    }

    private static orderByCreatedDateDescending(items: Array<HierarchicExecutionViewModel>): Array<HierarchicExecutionViewModel> {

        const parseTs = (s?: string | null) => {
            if (!s) return -Infinity
            const t = Date.parse(s)
            return Number.isFinite(t) ? t : -Infinity
        }

        const comparator = (a: HierarchicExecutionViewModel, b: HierarchicExecutionViewModel) => {
            const useStarted = !a.createdUtc || !b.createdUtc

            const ta = parseTs(useStarted ? a.startedUtc : a.createdUtc)
            const tb = parseTs(useStarted ? b.startedUtc : b.createdUtc)

            const aMissing = ta === -Infinity
            const bMissing = tb === -Infinity

            // Put items with missing timestamps BEFORE those with timestamps
            if (aMissing && !bMissing) return -1
            if (!aMissing && bMissing) return 1
            // Both missing: treat as equal → 0
            if (aMissing && bMissing) return 0;
            // Both have timestamps: descending
            return tb - ta
        }

        const sortRec = (nodes?: HierarchicExecutionViewModel[] | undefined): HierarchicExecutionViewModel[] => {
            if (!nodes || nodes.length === 0) return [];
            const sorted = [...nodes].sort(comparator);
            sorted.forEach(n => {
                if (n.children && n.children.length > 0) {
                    n.children = sortRec(n.children as HierarchicExecutionViewModel[]);
                }
            });
            return sorted;
        }

        return sortRec(items);
    }

    /**
     * Build a best-effort execution context for a report execution.
     * Walks the provided hierarchic `roots` to build an id->node map, then
     * walks the ancestor chain of `execution` using that map. It uses the
     * `primaryExecutionPhase` already annotated on nodes (or will call the
     * supplied `executionPhaseService` to compute it) to locate the nearest
     * CustomCalculations and Visualization nodes and returns the discovered ids.
     */
    public static buildExecutionContextModelForExecution = (
        execution: HierarchicExecutionViewModel,
        root: HierarchicExecutionViewModel,
        executionPhaseService?: IExecutionPhaseService
    ): ExecutionContextModel => {

        const result = {
            analysisFileId: execution?.operableInputFileId,
            customCalculationsExecutionId: undefined as string | undefined,
            postProcessingExecutionId: undefined as string | undefined,
            customReportsExecutionId: execution?.id
        } as ExecutionContextModel;

        if (!execution || !root?.id) return result;

        // Get analysisExectionId
        const firstRootId = root.id;
        result.analysisExecutionId = firstRootId;

        // build id map for fast parent lookups
        const idMap = new Map<string, HierarchicExecutionViewModel>();
        idMap.set(root.id, root);
        const walk = (nodes?: HierarchicExecutionViewModel[]) => {
            if (!nodes) return;
            nodes.forEach(n => {
                if (n.id) idMap.set(n.id, n);
                if (n.children && n.children.length) walk(n.children as HierarchicExecutionViewModel[]);
            });
        };
        walk(root?.children);

        const getPrimary = (node?: HierarchicExecutionViewModel) => {
            if (!node) return undefined as ExecutionPhaseType | undefined;
            if ((node as any).primaryExecutionPhase) return (node as any).primaryExecutionPhase as ExecutionPhaseType;
            if (executionPhaseService && typeof executionPhaseService.tryGetPrimaryPhaseTypeForExecution === 'function') {
                return executionPhaseService.tryGetPrimaryPhaseTypeForExecution(node as any);
            }
            return undefined as ExecutionPhaseType | undefined;
        };

        // Walk up the ancestor chain looking for Visualization and CustomCalculations
        let cur: HierarchicExecutionViewModel | undefined | null = execution;
        while (cur) {
            const primary = getPrimary(cur);

            if (!result.postProcessingExecutionId && primary === ExecutionPhaseType.Visualization) {
                result.postProcessingExecutionId = cur.id;
            }

            if (!result.customCalculationsExecutionId && primary === ExecutionPhaseType.CustomCalculations) {
                result.customCalculationsExecutionId = cur.id;
            }

            if (result.postProcessingExecutionId && result.customCalculationsExecutionId) break;

            if (!cur.parentId) break;
            cur = idMap.get(cur.parentId) ?? null;
        }

        return result;

    }

    // New: build hierarchic array from a filtered subset plus a source/unfiltered list
    public static buildHierarchicArrayFromFiltered(
        filteredList: Array<Execution>,
        sourceList: Array<Execution>,
        executionPhaseService: IExecutionPhaseService,
        includeAll: boolean = false
    ): Array<HierarchicExecutionViewModel> {
        // Guard
        if (!Array.isArray(filteredList) || filteredList.length === 0) return [];

        // Build source id -> item map (shallow clones to avoid mutating original)
        const sourceMap = new Map<string, HierarchicExecutionViewModel>();
        sourceList.forEach(item => {
            if (item.id) sourceMap.set(item.id, { ...item });
        });

        // Preserve original filtered ids so we only mark those as matched (ancestry should not be marked)
        const filteredIdSet = new Set<string>();
        filteredList.forEach(item => {
            if (item.id) filteredIdSet.add(item.id);
        });

        // Start with ids present in filteredList
        const includeIds = new Set<string>();
        filteredList.forEach(item => {
            if (item.id) includeIds.add(item.id);
        });

        // For each filtered item, include ancestry (parent chain) and rootId from the source map
        filteredList.forEach(item => {
            // include explicit rootId if present
            if (item.rootId) {
                includeIds.add(item.rootId);
            }

            // walk parent chain from this item using source map (stop if parent not found)
            let curParentId = item.parentId;
            while (curParentId) {
                if (includeIds.has(curParentId)) break;
                const parent = sourceMap.get(curParentId);
                if (!parent) break;
                includeIds.add(curParentId);

                // ensure parent's root is included
                if (parent.rootId) includeIds.add(parent.rootId);

                curParentId = parent.parentId;
            }
        });

        // When includeAll=true, expand includeIds to every item that shares a root with any matched item
        if (includeAll) {
            // Collect the root ids that have at least one matched item
            const matchedRootIds = new Set<string>();
            includeIds.forEach(id => {
                const item = sourceMap.get(id);
                if (item?.rootId) matchedRootIds.add(item.rootId);
                else if (item?.id) matchedRootIds.add(item.id); // item itself may be a root
            });

            // Add all source items belonging to those roots
            sourceList.forEach(item => {
                const belongsToMatchedRoot =
                    (item.rootId && matchedRootIds.has(item.rootId)) ||
                    (item.id && matchedRootIds.has(item.id));
                if (belongsToMatchedRoot && item.id) {
                    includeIds.add(item.id);
                }
            });
        }

        // Build a combined list in the same order as sourceList (keeps predictable ordering)
        const combined: HierarchicExecutionViewModel[] = [];
        sourceList.forEach(item => {
            if (item.id && includeIds.has(item.id)) {
                // use the cloned sourceMap item to avoid mutation of original
                const cloned = sourceMap.get(item.id);
                if (cloned) {
                    // mark as matched only if this id was present in the original filteredList
                    if (filteredIdSet.has(item.id)) cloned.hasMatchedSearch = true;
                    combined.push(cloned);
                }
            }
        });

        // Also ensure any filtered items not present in source (no id or missing) are included
        filteredList.forEach(item => {
            if (!item.id || !sourceMap.has(item.id)) {
                // mark these as matched since they originated from the filteredList
                combined.push({ ...item, hasMatchedSearch: true });
            }
        });

        // Delegate to existing builder to construct the final hierarchy
        return this.buildHierarchicArray(combined, executionPhaseService);
    }

    /**
     * Build a flat (non-hierarchic) ordered list from `flatList`.
     * Items are shallow-cloned, annotated with `operableInputFileId` and
     * `primaryExecutionPhase`, and sorted descending by started/completed date.
     * No parent-child nesting is performed.
     */
    public static buildArray(flatList: Array<Execution>, executionPhaseService: IExecutionPhaseService): Array<HierarchicExecutionViewModel> {
        if (!Array.isArray(flatList) || flatList.length === 0) return [];

        const items: HierarchicExecutionViewModel[] = flatList.map(item => ({ ...item }));

        // Build id -> item map for root-walking
        const idMap = new Map<string, HierarchicExecutionViewModel>();
        items.forEach(item => { if (item.id) idMap.set(item.id, item); });

        // Set operableInputFileId by walking up to the root for each item
        items.forEach(item => {
            let cur: HierarchicExecutionViewModel | undefined = item;
            while (cur) {
                if (!cur.parentId) {
                    item.operableInputFileId = cur.input?.fileId;
                    break;
                }
                const parent = idMap.get(cur.parentId);
                if (!parent) {
                    item.operableInputFileId = cur.input?.fileId;
                    break;
                }
                cur = parent;
            }
        });

        // Annotate primaryExecutionPhase
        if (executionPhaseService && typeof executionPhaseService.tryGetPrimaryPhaseTypeForExecution === "function") {
            items.forEach(item => {
                item.primaryExecutionPhase = ((item?.progress?.totalPhases ?? 0) > 1)
                    ? AdvancedAnalysisPhaseType
                    : executionPhaseService.tryGetPrimaryPhaseTypeForExecution(item);
            });
        }

        return this.orderByCreatedDateDescending(items);
    }

    /**
     * Build a flat ordered list from a filtered subset plus a source/unfiltered list.
     * Mirrors `buildHierarchicArrayFromFiltered` but delegates to `buildArray`
     * so no hierarchy is constructed.
     */
    public static buildArrayFromFiltered(
        filteredList: Array<Execution>,
        sourceList: Array<Execution>,
        executionPhaseService: IExecutionPhaseService
    ): Array<HierarchicExecutionViewModel> {
        if (!Array.isArray(filteredList) || filteredList.length === 0) return [];

        // Build source id -> item map (shallow clones to avoid mutating original)
        const sourceMap = new Map<string, HierarchicExecutionViewModel>();
        sourceList.forEach(item => { if (item.id) sourceMap.set(item.id, { ...item }); });

        // Track ids from the original filtered set for hasMatchedSearch annotation
        const filteredIdSet = new Set<string>();
        filteredList.forEach(item => { if (item.id) filteredIdSet.add(item.id); });

        // Start with ids present in filteredList
        const includeIds = new Set<string>();
        filteredList.forEach(item => { if (item.id) includeIds.add(item.id); });

        // For each filtered item, include ancestry (parent chain) and rootId from the source map
        filteredList.forEach(item => {
            if (item.rootId) includeIds.add(item.rootId);

            let curParentId = item.parentId;
            while (curParentId) {
                if (includeIds.has(curParentId)) break;
                const parent = sourceMap.get(curParentId);
                if (!parent) break;
                includeIds.add(curParentId);
                if (parent.rootId) includeIds.add(parent.rootId);
                curParentId = parent.parentId;
            }
        });

        // Build a combined list in the same order as sourceList
        const combined: HierarchicExecutionViewModel[] = [];
        sourceList.forEach(item => {
            if (item.id && includeIds.has(item.id)) {
                const cloned = sourceMap.get(item.id);
                if (cloned) {
                    if (filteredIdSet.has(item.id)) cloned.hasMatchedSearch = true;
                    combined.push(cloned);
                }
            }
        });

        // Include any filtered items not present in source
        filteredList.forEach(item => {
            if (!item.id || !sourceMap.has(item.id)) {
                combined.push({ ...item, hasMatchedSearch: true });
            }
        });

        // Build the full flat list (ancestors are included in `combined` solely so that
        // buildArray can walk the parent chain to resolve operableInputFileId).
        // Filter back down to only the items that originated from filteredList.
        const all = this.buildArray(combined, executionPhaseService);
        return all.filter(item => !item.id || filteredIdSet.has(item.id));
    }

    /**
     * Filter an already-built hierarchic tree by a set of matched flat ids.
     *
     * Pruning rules (applied recursively):
     * - Leaf nodes (no children) are always kept — they carry no subtree weight and
     *   provide sibling context around matched items.
     * - Nodes that have children are omitted when neither the node itself nor any
     *   descendant appears in `filteredFlatIds` (i.e. the entire branch is irrelevant).
     * - `hasMatchedSearch` is set on directly matched nodes.
     *
     * The result is a tree containing only the branches that lead to a match, so the
     * expand-all-when-filtered logic in ExecutionHistory naturally expands only those paths.
     */
    public static filterHierarchicArray(
        hierarchicList: Array<HierarchicExecutionViewModel>,
        filteredFlatIds: Set<string>,
        highlightIds?: Set<string>,
        usePerTreeHighlight?: boolean
    ): Array<HierarchicExecutionViewModel> {
        if (!filteredFlatIds.size) return [];

        // Memoized check: returns true if this node or any descendant is in filteredFlatIds.
        // Memoization ensures each node is visited at most once across both the pre-filter
        // pass and the pruneNode pass, keeping total work O(n).
        const matchCache = new Map<string, boolean>();
        const subtreeHasMatch = (node: HierarchicExecutionViewModel): boolean => {
            const key = node.id ?? '';
            if (key && matchCache.has(key)) return matchCache.get(key)!;
            const result =
                !!(node.id && filteredFlatIds.has(node.id)) ||
                !!(node.children as HierarchicExecutionViewModel[] | undefined)?.some(subtreeHasMatch);
            if (key) matchCache.set(key, result);
            return result;
        };

        // Returns true if the node or any descendant is absent from filteredFlatIds.
        // Used per-root to decide whether highlighting adds contrast: a tree where every
        // node already passes the filter has nothing to distinguish, so highlighting is skipped.
        const subtreeHasExcludedNode = (node: HierarchicExecutionViewModel): boolean => {
            if (node.id && !filteredFlatIds.has(node.id)) return true;
            const children = node.children as HierarchicExecutionViewModel[] | undefined;
            return !!children?.some(subtreeHasExcludedNode);
        };

        const effectiveHighlight = highlightIds ?? filteredFlatIds;

        // Recursively prune non-matching branches.
        // - Branch nodes (have children): omitted entirely when their subtree has no match.
        // - Leaf nodes (no children): always kept under an included parent as sibling context.
        // shouldHighlight is determined once per root and propagated down the subtree.
        const pruneNode = (
            node: HierarchicExecutionViewModel,
            shouldHighlight: boolean
        ): HierarchicExecutionViewModel | null => {
            const isMatch = shouldHighlight && !!(node.id && effectiveHighlight.has(node.id));
            const children = node.children as HierarchicExecutionViewModel[] | undefined;

            if (!children?.length) {
                // Leaf under an already-relevant parent: keep as context; mark if matched.
                return isMatch ? { ...node, hasMatchedSearch: true } : { ...node };
            }

            // Branch: discard the entire subtree if no descendant matches.
            if (!subtreeHasMatch(node)) return null;

            const prunedChildren = children
                .map((child) => pruneNode(child, shouldHighlight))
                .filter(Boolean) as HierarchicExecutionViewModel[];

            return { ...node, hasMatchedSearch: isMatch, children: prunedChildren };
        };

        // Pre-filter roots so that roots with zero matches (including those whose only
        // children are leaves) are excluded before pruneNode is even called on them.
        // When usePerTreeHighlight is true (date-filter-only mode), each root independently
        // decides whether its subtree warrants highlighting based on whether any of its nodes
        // are excluded. Otherwise all matched nodes are always highlighted.
        return hierarchicList
            .filter(subtreeHasMatch)
            .map((root) => pruneNode(root, usePerTreeHighlight ? subtreeHasExcludedNode(root) : true))
            .filter(Boolean) as HierarchicExecutionViewModel[];
    }

}